import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import type { QuizRunDetail } from '@/lib/api/quiz';
import { CheckCircle2, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';

interface QuizRunReviewModeProps {
  detail: QuizRunDetail;
  onBack: () => void;
  onGenerateDrill?: () => void;
}

function formatDomain(domain: string) {
  return domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const OPTION_KEYS = ['a', 'b', 'c', 'd'] as const;

export function QuizRunReviewMode({
  detail,
  onBack,
  onGenerateDrill,
}: QuizRunReviewModeProps) {
  const [wrongOnly, setWrongOnly] = useState(false);
  const [index, setIndex] = useState(0);

  const items = useMemo(() => {
    if (!wrongOnly) return detail.items;
    return detail.items.filter((item) => !item.is_correct);
  }, [detail.items, wrongOnly]);

  const current = items[index] ?? null;
  const total = items.length;
  const minutes = detail.time_taken_s != null ? Math.round(detail.time_taken_s / 60) : null;

  const handleWrongOnlyChange = (checked: boolean) => {
    setWrongOnly(checked);
    setIndex(0);
  };

  return (
    <div
      className="h-full flex flex-col min-h-0 overflow-hidden"
      data-testid={testIds.quizDocumentRenderer.reviewMode}
    >
      <div className="px-5 pt-4 pb-3 border-b border-border space-y-3 shrink-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                detail.passed ? 'bg-green-500/15 text-green-600' : 'bg-red-500/15 text-red-600'
              )}
            >
              {detail.passed ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <XCircle className="h-5 w-5" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold tabular-nums">{detail.score_pct}%</h3>
              <p className="text-sm text-muted-foreground">
                {detail.passed ? 'Passed' : 'Did not pass'} (passing: {detail.passing_score}%)
                {minutes != null ? ` - ${minutes} min` : ''}
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onBack}>
            Back to Runs
          </Button>
        </div>

        <div className="max-h-16 overflow-y-auto">
          <div className="flex items-center gap-1.5 flex-wrap">
            {Object.entries(detail.domain_scores || {})
              .sort((a, b) => b[1] - a[1])
              .map(([domain, pct]) => (
                <Badge
                  key={domain}
                  variant="outline"
                  className={cn(
                    'text-[11px] px-1.5 py-0',
                    pct < detail.passing_score
                      ? 'text-red-600 border-red-500/30'
                      : 'text-green-600 border-green-500/30'
                  )}
                >
                  {formatDomain(domain)} {pct}%
                </Badge>
              ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch
              id="quiz-review-wrong-only"
              checked={wrongOnly}
              onCheckedChange={handleWrongOnlyChange}
              data-testid={testIds.quizDocumentRenderer.reviewWrongOnly}
            />
            <Label htmlFor="quiz-review-wrong-only" className="text-sm text-muted-foreground">
              Wrong only
            </Label>
          </div>
          {detail.weak_domains?.length > 0 && onGenerateDrill && (
            <Button size="sm" variant="outline" onClick={onGenerateDrill}>
              Generate drill for weak domains
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
        {total === 0 ? (
          <p className="text-sm text-muted-foreground">
            {wrongOnly ? 'No incorrect answers in this run.' : 'No questions in this run.'}
          </p>
        ) : current ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Question {index + 1} / {total}
                {wrongOnly ? ' (wrong only)' : ''}
              </p>
              <Badge
                variant="outline"
                className={cn(
                  'text-xs',
                  current.is_correct
                    ? 'text-green-600 border-green-500/30'
                    : 'text-red-600 border-red-500/30'
                )}
              >
                {current.is_correct ? 'Correct' : 'Incorrect'}
              </Badge>
            </div>

            <div>
              <Badge variant="secondary" className="text-xs mb-2">
                {formatDomain(current.domain)}
              </Badge>
              <p className="text-sm font-medium leading-relaxed">{current.stem}</p>
            </div>

            <div className="space-y-2">
              {OPTION_KEYS.filter((key) => current.options[key] != null).map((key) => {
                const isUser = current.user_answer === key;
                const isCorrect = current.correct_answer === key;
                return (
                  <div
                    key={key}
                    className={cn(
                      'rounded-md border px-3 py-2 text-sm',
                      isCorrect && 'border-green-500/40 bg-green-500/10',
                      isUser && !isCorrect && 'border-red-500/40 bg-red-500/10',
                      !isUser && !isCorrect && 'border-border'
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <span className="font-medium uppercase shrink-0">{key}.</span>
                      <span className="flex-1">{current.options[key]}</span>
                      <span className="shrink-0 text-xs text-muted-foreground space-x-1">
                        {isUser && <span>Yours</span>}
                        {isCorrect && <span>Correct</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {!current.user_answer && (
              <p className="text-sm text-muted-foreground">You did not answer this question.</p>
            )}

            {current.explanation && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <p className="text-xs font-medium text-muted-foreground mb-1">Explanation</p>
                <p className="text-sm leading-relaxed">{current.explanation}</p>
              </div>
            )}
          </>
        ) : null}
      </div>

      {total > 0 && (
        <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            disabled={index <= 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            data-testid={testIds.quizDocumentRenderer.reviewPrev}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={index >= total - 1}
            onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
            data-testid={testIds.quizDocumentRenderer.reviewNext}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
